/**
 * Utility functions for timekeeping operations
 */
import {
  getDateInLocalTZFromDateStr,
  getShiftedDate,
  getWeekStart,
} from "./core";

/**
 * Checks if the given date is past the cutoff date (Tuesday of current week)
 * Used to determine if a timesheet for a specific date can still be edited
 *
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is past the cutoff, false otherwise
 */
export const isPastCutoff = (date) => {
  // Get Wednesday (day 3) of the current week as the cutoff date
  const cutoffDate = getShiftedDate(getWeekStart(date), 3);
  // If the current date is on or after the cutoff date, it's past the cutoff
  return date >= cutoffDate;
};

/**
 * Validates a shift's time against adjacent shifts
 * This function ensures a shift doesn't overlap with previous or next shifts
 *
 * @param {Object} shiftToValidate - The shift to validate
 * @param {Array} allShifts - All shifts for the day, ordered by time
 * @returns {Object} - Result object with validation status and error message if any
 */
export const validateShiftTimes = (shiftToValidate, allShifts) => {
  // If no shifts or only one shift, no conflicts possible
  if (!allShifts || allShifts.length <= 1) {
    return { valid: true };
  }

  // Filter out the current shift from all shifts (if it exists)
  const otherShifts = allShifts.filter(
    (shift) => shift.id !== shiftToValidate.id
  );

  // Sort shifts by startTime
  const sortedShifts = [...otherShifts].sort((a, b) =>
    a.startTime.localeCompare(b.startTime)
  );

  // Find previous and next shifts
  let prevShift = null;
  let nextShift = null;

  for (let i = 0; i < sortedShifts.length; i++) {
    if (sortedShifts[i].startTime > shiftToValidate.startTime) {
      nextShift = sortedShifts[i];
      if (i > 0) {
        prevShift = sortedShifts[i - 1];
      }
      break;
    } else if (i === sortedShifts.length - 1) {
      // If we reach the end, the shift to validate is the last one
      prevShift = sortedShifts[i];
    }
  }

  // If no previous or next shifts were found but there are other shifts,
  // this must be the first or last shift
  if (!prevShift && !nextShift && sortedShifts.length > 0) {
    // Must be the first shift
    if (shiftToValidate.startTime < sortedShifts[0].startTime) {
      nextShift = sortedShifts[0];
    }
    // Must be the last shift
    else {
      prevShift = sortedShifts[sortedShifts.length - 1];
    }
  }

  // Check for overlap with previous shift
  if (prevShift && shiftToValidate.startTime < prevShift.finishTime) {
    return {
      valid: false,
      error: "Start time must be after the previous shift's finish time",
      prevShift,
    };
  }

  // Check for overlap with next shift
  if (nextShift && shiftToValidate.finishTime > nextShift.startTime) {
    return {
      valid: false,
      error: "Finish time must be before the next shift's start time",
      nextShift,
    };
  }

  return {
    valid: true,
    prevShift,
    nextShift,
  };
};

/**
 * Finds all shifts with stage 'Clocked In' within weeklyShiftData
 *
 * @param {Object} weeklyData - The weekly shift data object
 * @returns {Array} - Array of objects containing date and shift information for clocked-in shifts
 */
export const findClockedInShifts = (weeklyData) => {
  if (!weeklyData || !weeklyData.days || !weeklyData.days.length) {
    return [];
  }

  const clockedInShifts = [];

  weeklyData.days.forEach((day) => {
    if (day.shifts && day.shifts.length) {
      const clockedInForDay = day.shifts.filter(
        (shift) => shift.stage === "Clocked In"
      );

      if (clockedInForDay.length > 0) {
        clockedInForDay.forEach((shift) => {
          clockedInShifts.push({
            date: day.date,
            dayOfWeek: day.day,
            shift,
          });
        });
      }
    }
  });

  return clockedInShifts;
};

/**
 * Returns the current time, if that time is within the given time window, otherwise,
 * returns the given default time.
 *
 * @param {string} startTime - The start time of the window in HH:mm (24hr) format
 * @param {string} endTime - The end time of the window in HH:mm (24hr) format
 * @param {string} defaultTime - The default time to return if current time is outside the window
 * @return {string} - The current time if within the window, otherwise the default time
 */
export const getCurrentTimeInWindow = (startTime, endTime, defaultTime) => {
  // Validate input times
  if (startTime === "" || endTime === "" || defaultTime === "") {
    return defaultTime;
  }

  const now = new Date();
  const currentTime = now.toTimeString().slice(0, 5); // Get HH:mm format

  // Convert times to Date objects for comparison
  const startDate = new Date(now.toDateString() + " " + startTime);
  const endDate = new Date(now.toDateString() + " " + endTime);
  const currentDate = new Date(now.toDateString() + " " + currentTime);

  // Check if current time is within the window
  if (currentDate >= startDate && currentDate <= endDate) {
    return currentTime;
  }

  return defaultTime;
};

/**
 * Return Sunday through Saturday Date objects for a Saturday week ending.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @returns {Array<Date>} Seven local dates in the payroll week.
 */
export function getPayrollWeekDates(weekEnding) {
  const weekEnd = getDateInLocalTZFromDateStr(weekEnding);
  const weekStart = getWeekStart(weekEnd);
  // Seven local calendar days from Sunday. Do not use getWeekOfDates here —
  // that helper counts millisecond spans and drops Saturday across spring DST.
  return Array.from({ length: 7 }, (_, dayOffset) =>
    getShiftedDate(weekStart, dayOffset)
  );
}
